// Global packages and components
import clsx from 'clsx'
import { formatDate, getCurrencySymbol, formatPrice } from '~/utils'
import { ArchiveBoxIcon, TruckIcon } from '@heroicons/react/24/outline'
import { FulfilmentButton, InlineLink } from '~/components'

// Types
import type {
  BrendaChargesDelivery,
  BrendaChargesPickup,
} from '~/types/brendaCharges'

interface Props {
  delivery: BrendaChargesDelivery | null
  pickup: BrendaChargesPickup | null
  fulfilment: string | null
  setFulfilment?: (e: string) => void
  deliveryOnly?: boolean
  unavailableDeliveryDate?: string | null
  name?: string
  /**
   * When provided and pickup is available, renders a "Change branch"
   * link under the Click & Collect option. The link is always shown,
   * but only opens the branch picker once Click & Collect is actually
   * selected — see `handleChangeBranch`. Omitted in basket usage,
   * where branch changes are driven from the line item instead.
   */
  onChangeBranch?: () => void
  /**
   * Delivery address label. When provided, the delivery option's
   * wording reads "…to {address}…". Omitted in basket usage, so its
   * wording is unchanged.
   */
  address?: string | null
}

/**
 * Fulfilment toggle — renders delivery + pickup buttons with their
 * dates, prices and stock info. Fully prop-driven so it can be used
 * in both the PDP and basket contexts.
 */
const FulfilmentButtons = ({
  address,
  delivery,
  pickup,
  fulfilment,
  setFulfilment,
  deliveryOnly = false,
  unavailableDeliveryDate,
  name = 'pdp-fulfil',
  onChangeBranch,
}: Props) => {
  // Staged so one click can never both switch fulfilment and open the picker —
  // the reason this link used to be hidden unless C&C was already selected.
  // A <label> doesn't forward clicks to its control when the target is an
  // interactive descendant, and this link is a <button>, so selecting C&C has
  // to be done explicitly rather than left to the surrounding label. First
  // click moves the customer onto C&C; the picker opens on the next one.
  const handleChangeBranch = () => {
    if (fulfilment !== 'pickup') {
      setFulfilment?.('pickup')

      return
    }

    onChangeBranch?.()
  }

  return (
    <div
      className={clsx('grid grid-cols-1 gap-3 sm:gap-5', {
        'sm:grid-cols-2': !deliveryOnly,
      })}
    >
      {!deliveryOnly && (
        <FulfilmentButton
          name={name}
          id={`${name}-pickup`}
          value="pickup"
          checked={fulfilment === 'pickup'}
          disabled={!pickup?.available || !pickup.date}
          onChange={() => {
            setFulfilment && setFulfilment('pickup')
          }}
        >
          <div className="shrink-0 pt-1">
            <ArchiveBoxIcon className="size-5 text-teal-green" />
          </div>
          <div>
            <p className="mb-1 text-base font-semibold">Click & Collect</p>
            {pickup?.available && pickup.date ? (
              <>
                <p className="mb-2 text-sm">
                  Collection available from{' '}
                  <span className="font-semibold text-teal-green">
                    {formatDate(pickup.date)}
                  </span>
                  {pickup.branchPostcode && (
                    <>
                      &nbsp;at&nbsp;
                      <span className="font-semibold">
                        {pickup.branchPostcode}
                      </span>
                    </>
                  )}
                </p>
                <p className="mb-0 text-xs font-semibold">
                  {pickup.maxQuantity <= 0 ? (
                    <span className="text-neutral-500">Out of stock</span>
                  ) : pickup.stockIsFallback ? (
                    <>Stock available</>
                  ) : (
                    <>{pickup.maxQuantity}&nbsp;in stock</>
                  )}
                </p>
                {pickup.branchAddressLine1 && (
                  <div className="mt-2 border-t pt-2">
                    <p className="mb-0 text-xs">
                      {pickup.branchAddressLine1}
                      {pickup.branchPostcode && `, ${pickup.branchPostcode}`}
                    </p>
                    {onChangeBranch && (
                      <div>
                        <InlineLink
                          label="Change branch"
                          size="xs"
                          onClick={handleChangeBranch}
                        />
                      </div>
                    )}
                  </div>
                )}
              </>
            ) : (
              <p className="mb-0 text-sm">
                Collection unavailable for the current selection
              </p>
            )}
          </div>
        </FulfilmentButton>
      )}
      <FulfilmentButton
        name={name}
        id={`${name}-delivery`}
        value="delivery"
        checked={fulfilment === 'delivery'}
        disabled={!delivery?.available || !delivery.date}
        onChange={() => {
          setFulfilment && setFulfilment('delivery')
        }}
      >
        <div className="shrink-0 pt-1">
          <TruckIcon className="size-6 text-teal-green" />
        </div>
        <div>
          <p className="mb-1 text-base font-semibold">Delivery</p>
          {delivery?.date && unavailableDeliveryDate && (
            <p className="mb-0.5 text-sm">
              Delivery unavailable on&nbsp;
              <span className="font-semibold text-red-600">
                {formatDate(unavailableDeliveryDate)}
              </span>
            </p>
          )}
          {delivery?.available && delivery.date ? (
            <>
              <p className="mb-2 text-sm">
                {unavailableDeliveryDate
                  ? 'Next delivery date available on'
                  : 'Delivery available on'}
                &nbsp;
                <span className="font-semibold text-teal-green">
                  {formatDate(delivery.date)}
                </span>
              </p>
              {delivery.price?.amount && delivery.price.amount !== 0 ? (
                <p className="mb-0 text-xs font-semibold">
                  {getCurrencySymbol('GBP') +
                    formatPrice(delivery.price.amount)}
                </p>
              ) : (
                <p className="mb-0 text-xs font-semibold">Free delivery</p>
              )}
              {address && pickup?.available && pickup.date && (
                <p className="mb-0 mt-2 border-t pt-2 text-xs">{address}</p>
              )}
            </>
          ) : (
            <p className="mb-0 text-xs">Delivery unavailable</p>
          )}
        </div>
      </FulfilmentButton>
    </div>
  )
}

export default FulfilmentButtons
